import { supabase } from "@/lib/supabase";
import type { AppRole } from "@/lib/roles";

/** Matches admin UI rows built from profiles + user_roles + property_owners without PostgREST embeds. */
export type AdminProfileDirectoryRow = {
  id: string;
  name: string | null;
  email: string;
  phone: string | null;
  address: string | null;
  management_fee: number | null;
  management_fee_rooms: number | null;
  is_active: boolean;
  /**
   * COL-447 — presentation-only demo flag (see the column comment in
   * 20260920100000_profiles_is_demo_account.sql). Read here so the Owners list
   * can show which account it is and offer the admin toggle; it grants nothing.
   */
  is_demo_account: boolean;
  created_at: string;
  user_roles: { role: AppRole }[];
  property_owners: {
    property_id: number;
    bank_account_name: string | null;
    bank_account_iban: string | null;
    bank_account_bic: string | null;
    lessor_id: string | null;
    contract_required: boolean;
    contract_template_ids: string[];
  }[];
  /** Latest onboarding token status for this email, or null if no token exists. */
  onboarding_token_status: "pending" | "completed" | "expired" | null;
};

type RawPropertyOwner = {
  user_id: string;
  property_id: number;
  bank_account_name: string | null;
  bank_account_iban: string | null;
  bank_account_bic: string | null;
  lessor_id: string | null;
  contract_required: boolean | null;
  contract_template_ids: string[] | null;
};

/**
 * Fetch property_owners including the per-property contract columns. Falls back
 * to the legacy column set (defaulting the contract fields) when the
 * `contract_required` / `contract_template_ids` migration hasn't been applied
 * yet — so the admin directory never hard-breaks on deploy ordering.
 */
async function fetchPropertyOwners(): Promise<RawPropertyOwner[]> {
  const withContract = await supabase
    .from("property_owners")
    .select(
      "user_id, property_id, bank_account_name, bank_account_iban, bank_account_bic, lessor_id, contract_required, contract_template_ids"
    );
  if (!withContract.error) {
    return (withContract.data ?? []) as RawPropertyOwner[];
  }

  const legacy = await supabase
    .from("property_owners")
    .select(
      "user_id, property_id, bank_account_name, bank_account_iban, bank_account_bic, lessor_id"
    );
  if (legacy.error) throw legacy.error;
  return (legacy.data ?? []).map((o) => ({
    ...(o as Omit<
      RawPropertyOwner,
      "contract_required" | "contract_template_ids"
    >),
    contract_required: true,
    contract_template_ids: [],
  }));
}

type RawProfile = Record<string, unknown> & {
  id: string;
  email: string;
};

const PROFILE_COLUMNS =
  "id, name, email, phone, address, management_fee, management_fee_rooms, is_active, created_at";

/**
 * Fetch the directory's profile rows, including the COL-447 demo flag.
 *
 * Falls back to the column set without `is_demo_account` when that migration
 * has not been applied yet — same reason as `fetchPropertyOwners` above: this
 * one query backs both the Directory and the Owners list, so a deploy that
 * ships the frontend ahead of the migration would otherwise blank both screens
 * rather than merely hide one toggle.
 */
async function fetchProfileRows(): Promise<RawProfile[]> {
  const withDemo = await supabase
    .from("profiles")
    .select(`${PROFILE_COLUMNS}, is_demo_account`)
    .order("created_at", { ascending: false });
  if (!withDemo.error) return (withDemo.data ?? []) as RawProfile[];

  const legacy = await supabase
    .from("profiles")
    .select(PROFILE_COLUMNS)
    .order("created_at", { ascending: false });
  if (legacy.error) throw legacy.error;
  return (legacy.data ?? []) as RawProfile[];
}

/** Parallel fetches avoid PGRST200 when no FK exists between profiles and user_roles for embedding. */
export async function fetchAdminProfilesDirectory(): Promise<
  AdminProfileDirectoryRow[]
> {
  const [profileRows, rolesRes, ownersRows, tokensRes] = await Promise.all([
    fetchProfileRows(),
    supabase.from("user_roles").select("user_id, role"),
    fetchPropertyOwners(),
    supabase
      .from("owner_onboarding_tokens")
      .select("email, status, created_at")
      .order("created_at", { ascending: false }),
  ]);

  if (rolesRes.error) throw rolesRes.error;

  const rolesMap = new Map<string, { role: AppRole }[]>();
  for (const r of rolesRes.data ?? []) {
    const uid = r.user_id as string;
    const arr = rolesMap.get(uid) ?? [];
    arr.push({ role: r.role as AppRole });
    rolesMap.set(uid, arr);
  }

  const ownersMap = new Map<
    string,
    AdminProfileDirectoryRow["property_owners"]
  >();
  for (const o of ownersRows) {
    const uid = o.user_id as string;
    const arr = ownersMap.get(uid) ?? [];
    arr.push({
      property_id: Number(o.property_id),
      bank_account_name: (o.bank_account_name as string | null) ?? null,
      bank_account_iban: (o.bank_account_iban as string | null) ?? null,
      bank_account_bic: (o.bank_account_bic as string | null) ?? null,
      lessor_id: (o.lessor_id as string | null) ?? null,
      contract_required: (o.contract_required as boolean | null) ?? true,
      contract_template_ids: (o.contract_template_ids as string[] | null) ?? [],
    });
    ownersMap.set(uid, arr);
  }

  // Keep only the latest token per email (results already ordered desc by created_at)
  const tokenMap = new Map<string, "pending" | "completed" | "expired">();
  for (const t of tokensRes.data ?? []) {
    const email = t.email as string;
    if (!tokenMap.has(email)) {
      tokenMap.set(email, t.status as "pending" | "completed" | "expired");
    }
  }

  return profileRows.map((p) => ({
    ...(p as unknown as AdminProfileDirectoryRow),
    phone: (p.phone as string | null) ?? null,
    address: (p.address as string | null) ?? null,
    management_fee: (p.management_fee as number | null) ?? null,
    management_fee_rooms: (p.management_fee_rooms as number | null) ?? null,
    is_active: (p.is_active as boolean | null) ?? true,
    // Absent on a pre-migration database (see fetchProfileRows) — false there
    // is the same answer the column's own default gives.
    is_demo_account: (p.is_demo_account as boolean | null) ?? false,
    user_roles: rolesMap.get(p.id) ?? [],
    property_owners: ownersMap.get(p.id) ?? [],
    onboarding_token_status: tokenMap.get(p.email) ?? null,
  }));
}

/** Full profile rows with merged roles (no embed) — for legacy Admin Users table. */
export async function fetchProfilesWithRolesMerged(): Promise<
  Array<
    Record<string, unknown> & { id: string; user_roles: { role: AppRole }[] }
  >
> {
  const [profilesRes, rolesRes] = await Promise.all([
    supabase
      .from("profiles")
      .select("*")
      .order("created_at", { ascending: false }),
    supabase.from("user_roles").select("user_id, role"),
  ]);

  if (profilesRes.error) throw profilesRes.error;
  if (rolesRes.error) throw rolesRes.error;

  const rolesMap = new Map<string, { role: AppRole }[]>();
  for (const r of rolesRes.data ?? []) {
    const uid = r.user_id as string;
    const arr = rolesMap.get(uid) ?? [];
    arr.push({ role: r.role as AppRole });
    rolesMap.set(uid, arr);
  }

  return (profilesRes.data ?? []).map((p) => {
    const row = p as Record<string, unknown> & { id: string };
    return {
      ...row,
      user_roles: rolesMap.get(row.id) ?? [],
    };
  });
}
